import { useEffect, useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  Building2,
  Users,
  Send,
  ShieldCheck,
  Database,
  BarChart3,
  LifeBuoy,
  Wrench,
  Settings,
  HelpCircle,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  LogOut,
  Search as SearchIcon,
  Bell,
  ShieldAlert,
  FileClock,
  Package,
  ClipboardList,
  ReceiptText,
  Languages,
  Settings2,
  FileText,
  Trash2,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/lib/i18n";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import GlobalSearch from "@/components/GlobalSearch";
import QuickToolsDrawer from "@/components/QuickToolsDrawer";

// ---------------- Admin Role → visible groups matrix ----------------
// Phase 1 sidebar (per Product spec):
//   OVERVIEW · PLATFORM · OPERATIONS · MASTER DATA · GROWTH · SUPPORT · ADMIN
// Change Requests / Notification Templates / Reports & Exports are still
// wired as routes but hidden from the sidebar per Phase 1 launch scope.
const ADMIN_GROUPS = [
  {
    key: "overview",
    label: "Overview",
    items: [
      { to: "/admin/dashboard", label: "Dashboard", Icon: LayoutDashboard, roles: ["*"] },
    ],
  },
  {
    key: "platform",
    label: "Platform",
    items: [
      { to: "/admin/garages", label: "Garages", Icon: Building2, roles: ["platform_owner", "platform_admin", "support_admin", "analytics_admin", "compliance_admin", "readonly_exec"] },
      { to: "/admin/users",   label: "Users",   Icon: Users,     roles: ["platform_owner", "platform_admin", "support_admin", "analytics_admin", "compliance_admin", "readonly_exec"] },
    ],
  },
  {
    key: "operations",
    label: "Operations",
    items: [
      { to: "/admin/enquiries",    label: "Parts Enquiries", Icon: Send,          roles: ["platform_owner", "platform_admin", "sales_admin", "sales_manager", "support_admin", "compliance_admin", "readonly_exec"] },
      { to: "/admin/job-cards",    label: "Job Cards",       Icon: ClipboardList, roles: ["platform_owner", "platform_admin", "sales_admin", "sales_manager", "support_admin", "compliance_admin", "readonly_exec", "analytics_admin"] },
      { to: "/admin/estimates",    label: "Estimates",       Icon: FileText,      roles: ["platform_owner", "platform_admin", "sales_admin", "sales_manager", "support_admin", "compliance_admin", "readonly_exec", "analytics_admin"] },
      { to: "/admin/invoices",     label: "Invoices",        Icon: ReceiptText,   roles: ["platform_owner", "platform_admin", "compliance_admin", "analytics_admin", "readonly_exec"] },
      { to: "/admin/inventory",    label: "Inventory",       Icon: Package,       roles: ["platform_owner", "platform_admin", "analytics_admin", "readonly_exec"] },
    ],
  },
  {
    key: "master_data",
    label: "Master Data",
    items: [
      { to: "/admin/master-data/makes",           label: "Makes",           Icon: Database, roles: ["platform_owner", "master_data_admin", "platform_admin"] },
      { to: "/admin/master-data/models",          label: "Models",          Icon: Database, roles: ["platform_owner", "master_data_admin", "platform_admin"] },
      { to: "/admin/master-data/service-tasks",   label: "Service Tasks",   Icon: Wrench,   roles: ["platform_owner", "master_data_admin", "platform_admin"] },
      { to: "/admin/master-data/spare-parts",     label: "Spare Parts",     Icon: Package,  roles: ["platform_owner", "master_data_admin", "platform_admin"] },
      { to: "/admin/master-data/part-categories", label: "Part Categories", Icon: Database, roles: ["platform_owner", "master_data_admin", "platform_admin"] },
      { to: "/admin/master-data/translations",    label: "Translations",    Icon: Languages, roles: ["platform_owner", "translation_admin", "master_data_admin"] },
    ],
  },
  {
    key: "growth",
    label: "Growth",
    items: [
      { to: "/admin/adoption", label: "Adoption Analytics", Icon: BarChart3, roles: ["platform_owner", "platform_admin", "analytics_admin", "readonly_exec", "compliance_admin"] },
    ],
  },
  {
    key: "support",
    label: "Support",
    items: [
      { to: "/admin/support/cases", label: "Support Cases", Icon: LifeBuoy,  roles: ["platform_owner", "platform_admin", "support_admin", "compliance_admin", "readonly_exec"] },
      { to: "/admin/sync-monitor",  label: "Sync Monitor",  Icon: FileClock, roles: ["platform_owner", "platform_admin", "support_admin", "tech_ops_admin", "analytics_admin", "compliance_admin", "readonly_exec"] },
    ],
  },
  {
    key: "admin",
    label: "Admin",
    items: [
      { to: "/admin/members",   label: "Admin Members",     Icon: ShieldAlert, roles: ["platform_owner", "platform_admin", "compliance_admin"] },
      { to: "/admin/deletion-requests", label: "Deletion Requests", Icon: Trash2, roles: ["platform_owner", "platform_admin", "support_admin", "compliance_admin", "readonly_exec"] },
      { to: "/admin/audit-log", label: "Audit Log",         Icon: ShieldCheck, roles: ["platform_owner", "platform_admin", "compliance_admin"] },
      { to: "/admin/settings",  label: "Platform Settings", Icon: Settings2,   roles: ["platform_owner", "platform_admin", "compliance_admin", "readonly_exec", "support_admin", "master_data_admin", "translation_admin", "analytics_admin", "tech_ops_admin", "sales_admin", "sales_manager"] },
    ],
  },
];

// Nice human labels for the 11 admin roles
const ADMIN_ROLE_LABEL = {
  platform_owner: "Platform Owner",
  platform_admin: "Platform Admin",
  sales_admin: "Sales Admin",
  sales_manager: "Sales Manager",
  support_admin: "Support Admin",
  master_data_admin: "Master Data Admin",
  translation_admin: "Translation Admin",
  analytics_admin: "Analytics Admin",
  tech_ops_admin: "Tech Ops Admin",
  compliance_admin: "Compliance & Audit",
  readonly_exec: "Read-Only Executive",
};

const canSee = (item, role) => item.roles.includes("*") || item.roles.includes(role) || role === "platform_owner";

// ---------------- Sidebar ----------------
const SidebarItem = ({ item, collapsed }) => (
  <NavLink
    to={item.to}
    onClick={(e) => { if (item.soon) e.preventDefault(); }}
    data-testid={`admin-nav-${item.to.replace(/\//g, "-").slice(1)}`}
    className={({ isActive }) =>
      `group relative flex items-center ${collapsed ? "justify-center px-2" : "gap-3 px-3"} h-10 mx-2 rounded-md text-[13px] transition-colors ${
        isActive && !item.soon
          ? "bg-[#e63946] text-white font-semibold"
          : item.soon
          ? "text-[#a8dadc]/60 cursor-not-allowed"
          : "text-[#a8dadc] hover:bg-white/5 hover:text-white"
      }`
    }
    title={collapsed ? `${item.label}${item.soon ? " — coming soon" : ""}` : undefined}
  >
    <item.Icon size={16} strokeWidth={2} />
    {!collapsed && <span className="flex-1 truncate">{item.label}</span>}
    {!collapsed && item.soon && (
      <span className="text-[9px] font-bold uppercase tracking-wider bg-white/10 text-[#a8dadc] px-1.5 py-0.5 rounded">
        Soon
      </span>
    )}
  </NavLink>
);

const AdminSidebar = ({ collapsed, onToggle }) => {
  const { user } = useAuth();
  const role = user?.admin_role || "";
  return (
    <aside
      className={`shrink-0 bg-[#1d3557] text-white flex flex-col h-screen sticky top-0 transition-[width] duration-200 ${
        collapsed ? "w-[80px]" : "w-[260px]"
      }`}
      data-testid="admin-sidebar"
    >
      <div className="px-3 py-4 border-b border-white/10 flex items-center gap-2">
        <img src="/fender-icon.png" alt="Fender" className="w-9 h-9 shrink-0" />
        {!collapsed && (
          <div className="min-w-0 flex-1">
            <div className="font-bold text-[15px] leading-tight text-white flex items-center gap-1.5">
              Fender
              <span className="text-[9px] font-bold uppercase tracking-widest bg-[#e63946] text-white px-1.5 py-0.5 rounded">
                Admin
              </span>
            </div>
            <div className="text-[10px] uppercase tracking-widest text-[#a8dadc]">by SparesHub</div>
          </div>
        )}
        <button
          onClick={onToggle}
          data-testid="admin-sidebar-toggle"
          className="p-1 rounded hover:bg-white/10 text-[#a8dadc] hover:text-white"
          title={collapsed ? "Expand" : "Collapse"}
        >
          {collapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
        </button>
      </div>

      {!collapsed && (
        <div className="px-4 pt-3 pb-2 text-[10px] uppercase tracking-widest font-semibold text-[#a8dadc] truncate">
          Production · {ADMIN_ROLE_LABEL[role] || role || "Admin"}
        </div>
      )}

      <nav className="flex-1 overflow-y-auto no-scrollbar py-2">
        {ADMIN_GROUPS.map((g) => {
          const items = g.items.filter((it) => canSee(it, role));
          if (!items.length) return null;
          return (
            <div key={g.key} className="pt-2">
              {!collapsed && (
                <div className="px-5 pb-1 text-[10px] uppercase tracking-[0.16em] font-bold text-[#a8dadc]/70">
                  {g.label}
                </div>
              )}
              {collapsed && <div className="mx-3 my-1 border-t border-white/5" />}
              {items.map((it) => (
                <SidebarItem key={it.to} item={it} collapsed={collapsed} />
              ))}
            </div>
          );
        })}
      </nav>

      <div className="border-t border-white/10 p-3">
        {!collapsed ? <AdminUserBox /> : (
          <div className="grid place-items-center">
            <AdminAvatar small />
          </div>
        )}
      </div>
    </aside>
  );
};

const AdminAvatar = ({ small }) => {
  const { user } = useAuth();
  const initial = (user?.name || user?.email || "?")[0]?.toUpperCase();
  return (
    <div className={`${small ? "w-8 h-8 text-[12px]" : "w-9 h-9 text-[13px]"} rounded-full bg-[#e63946] text-white font-bold grid place-items-center`}>
      {initial}
    </div>
  );
};

const AdminUserBox = () => {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button data-testid="admin-userbox-btn" className="w-full flex items-center gap-2 rounded-md hover:bg-white/5 p-2 text-left">
          <AdminAvatar />
          <div className="min-w-0 flex-1">
            <div className="text-[13px] font-semibold text-white truncate" data-testid="admin-user-name">
              {user?.name || user?.email}
            </div>
            <div className="text-[10px] uppercase tracking-widest text-[#a8dadc] font-bold truncate">
              {ADMIN_ROLE_LABEL[user?.admin_role] || user?.admin_role}
            </div>
          </div>
          <ChevronDown size={13} className="text-[#a8dadc]" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent side="top" align="start" className="w-56 rounded-lg bg-white border border-[#E4E7EC] shadow-lg p-1">
        <DropdownMenuLabel className="text-[11px] uppercase tracking-wider text-[#667085] font-semibold">Admin</DropdownMenuLabel>
        <DropdownMenuSeparator className="bg-[#E4E7EC]" />
        <DropdownMenuItem
          onClick={() => nav("/admin/members")}
          className="cursor-pointer rounded-md text-[13px] hover:bg-[#F1FAEE] focus:bg-[#F1FAEE] px-2 py-2 flex items-center gap-2"
        >
          <ShieldAlert size={14} className="text-[#457b9d]" /> Admin members
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={() => window.open("mailto:contact@fenderapp.com", "_blank")}
          className="cursor-pointer rounded-md text-[13px] hover:bg-[#F1FAEE] focus:bg-[#F1FAEE] px-2 py-2 flex items-center gap-2"
        >
          <HelpCircle size={14} className="text-[#457b9d]" /> Help
        </DropdownMenuItem>
        <DropdownMenuSeparator className="bg-[#E4E7EC]" />
        <DropdownMenuItem
          onClick={() => { logout(); nav("/"); }}
          data-testid="admin-logout-btn"
          className="cursor-pointer rounded-md text-[13px] text-[#e63946] hover:bg-[#e63946]/10 focus:bg-[#e63946]/10 px-2 py-2 flex items-center gap-2"
        >
          <LogOut size={14} /> Sign out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

// ---------------- Breadcrumbs for admin ----------------
const CRUMB_LABELS = {
  admin: "Admin",
  dashboard: "Dashboard",
  garages: "Garages",
  users: "Users",
  enquiries: "Parts Enquiries",
  members: "Admin Members",
  "audit-log": "Audit Log",
  "master-data": "Master Data",
  "makes": "Makes",
  "models": "Models",
  "service-tasks": "Service Tasks",
  "translations": "Translations",
  "support": "Support",
  "cases": "Support Cases",
  "sync-monitor": "Sync Monitor",
  "change-requests": "Change Requests",
  "settings": "Platform Settings",
  "reports": "Reports & Exports",
  "adoption": "Adoption Analytics",
  "templates": "Notification Templates",
  "job-cards": "Job Cards",
  "estimates": "Estimates",
  "invoices": "Invoices",
  "appointments": "Appointments",
  "inventory": "Inventory Overview",
  "spare-parts": "Spare Parts",
  "part-categories": "Part Categories",
};
const AdminBreadcrumbs = () => {
  const loc = useLocation();
  const parts = loc.pathname.split("/").filter(Boolean);
  return (
    <div className="hidden md:flex items-center gap-1 text-[12px] text-[#667085] min-w-0">
      {parts.map((p, i) => (
        <span key={`${p}-${i}`} className="inline-flex items-center gap-1 min-w-0">
          {i > 0 && <span className="text-[#a8dadc]">/</span>}
          <span className={`truncate ${i === parts.length - 1 ? "text-[#1d3557] font-semibold" : ""}`}>
            {CRUMB_LABELS[p] || p.replace(/-/g, " ")}
          </span>
        </span>
      ))}
    </div>
  );
};

// ---------------- Top Header ----------------
const AdminTopHeader = ({ onOpenSearch, onOpenQT }) => {
  const { t } = useI18n();
  return (
    <header className="h-14 bg-white border-b border-[#E4E7EC] flex items-center gap-3 px-4 md:px-6 sticky top-0 z-30" data-testid="admin-topheader">
      <AdminBreadcrumbs />
      <div className="flex-1 flex justify-center max-w-xl mx-auto">
        <button
          onClick={onOpenSearch}
          data-testid="admin-search-trigger"
          className="hidden sm:flex items-center gap-2 w-full max-w-md h-9 px-3 rounded-md bg-[#F7F9FC] border border-[#E4E7EC] text-[13px] text-[#667085] hover:border-[#457b9d] hover:bg-white transition-colors"
        >
          <SearchIcon size={14} />
          <span className="flex-1 text-left">{t("search_placeholder") || "Search garages, users, enquiries…"}</span>
          <kbd className="hidden md:inline-block text-[10px] font-mono text-[#667085] bg-white border border-[#E4E7EC] rounded px-1.5">
            Ctrl K
          </kbd>
        </button>
      </div>

      <div className="flex items-center gap-1.5">
        <span
          data-testid="admin-env-pill"
          className="hidden sm:inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-widest text-[#e63946] bg-[#e63946]/10 border border-[#e63946]/25 rounded-md px-2 py-1"
        >
          Production
        </span>
        <button
          onClick={onOpenQT}
          data-testid="admin-quicktools-btn"
          className="w-9 h-9 grid place-items-center rounded-md text-[#457b9d] hover:bg-[#F1FAEE] hover:text-[#1d3557]"
          title="Quick Tools"
        >
          <Wrench size={16} />
        </button>
        <button
          data-testid="admin-notifications-btn"
          className="w-9 h-9 grid place-items-center rounded-md text-[#457b9d] hover:bg-[#F1FAEE] hover:text-[#1d3557]"
          title="Notifications"
        >
          <Bell size={16} />
        </button>
      </div>
    </header>
  );
};

// ---------------- Admin Shell ----------------
export default function AdminShell() {
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem("fender_admin_sidebar_collapsed") === "1");
  const [searchOpen, setSearchOpen] = useState(false);
  const [qtOpen, setQtOpen] = useState(false);

  useEffect(() => {
    localStorage.setItem("fender_admin_sidebar_collapsed", collapsed ? "1" : "0");
  }, [collapsed]);

  useEffect(() => {
    const onKey = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setSearchOpen((v) => !v);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  return (
    <div
      className="min-h-screen flex bg-[#F7F9FC]"
      style={{ fontFamily: '"Noto Sans","Noto Sans Devanagari","Noto Sans Kannada","Noto Sans Tamil","Noto Sans Malayalam",Arial,sans-serif' }}
      data-testid="admin-shell"
    >
      <AdminSidebar collapsed={collapsed} onToggle={() => setCollapsed((v) => !v)} />
      <div className="flex-1 flex flex-col min-w-0">
        <AdminTopHeader onOpenSearch={() => setSearchOpen(true)} onOpenQT={() => setQtOpen(true)} />
        <main className="flex-1 p-4 md:p-8 min-w-0">
          <Outlet />
        </main>
      </div>
      <GlobalSearch open={searchOpen} onOpenChange={setSearchOpen} />
      <QuickToolsDrawer open={qtOpen} onOpenChange={setQtOpen} />
    </div>
  );
}

export { ADMIN_ROLE_LABEL };
